import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Feedback } from '../components/Feedback';
import { IntegrationsTab } from '../components/IntegrationsTab';
import { LogoSeal } from '../components/Logo';
import { OverlayConfigTab } from '../components/OverlayConfigTab';
import { PLATFORM_LABEL } from '../lib/platforms';
import { useAuth } from '../contexts/auth-context';
import { formatCurrency, formatDate } from '../lib/format';
import {
  fetchDashboardMessages,
  fetchFinance,
  updateMessageStatus,
  type DashboardMessage,
  type FinanceSummary,
  type MessageStatus,
} from '../services/api';

type Tab = 'messages' | 'finance' | 'overlay' | 'integrations';
type StatusFilter = MessageStatus | 'all';

const STATUS_LABEL: Record<MessageStatus, string> = {
  new: 'Nova',
  read: 'Lida',
  hidden: 'Oculta',
};

const FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Todas' },
  { value: 'new', label: 'Novas' },
  { value: 'read', label: 'Lidas' },
  { value: 'hidden', label: 'Ocultas' },
];

/**
 * Dashboard do criador: aba de mensagens (com moderação) e aba financeira.
 * Rota protegida — só renderiza com sessão válida (ver ProtectedRoute).
 */
export function DashboardPage() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState<Tab>('messages');

  return (
    <div className="dashboard">
      <header className="dashboard__header">
        <div className="dashboard__brand">
          <LogoSeal size={32} />
        </div>
        <div className="dashboard__greeting">
          <h1 className="dashboard__title">Olá, {user?.name}</h1>
          <p className="dashboard__subtitle">
            Sua página de mensagens: <code>/{user?.username}</code>
            {/* Só quem é admin de fato vê a porta — e mesmo assim é a API que
                decide se ela abre (ver require-admin.ts). */}
            {user?.isAdmin && (
              <>
                {' · '}
                <Link to="/admin">painel de administração</Link>
              </>
            )}
          </p>
        </div>
        <button className="button button--ghost" type="button" onClick={logout}>
          Sair
        </button>
      </header>

      {/* Navegação por abas — vira barra fixa e confortável no mobile */}
      <nav className="dashboard__tabs" aria-label="Seções da dashboard">
        <button
          className={`dashboard__tab${tab === 'messages' ? ' dashboard__tab--active' : ''}`}
          type="button"
          onClick={() => setTab('messages')}
        >
          Mensagens
        </button>
        <button
          className={`dashboard__tab${tab === 'finance' ? ' dashboard__tab--active' : ''}`}
          type="button"
          onClick={() => setTab('finance')}
        >
          Financeiro
        </button>
        <button
          className={`dashboard__tab${tab === 'overlay' ? ' dashboard__tab--active' : ''}`}
          type="button"
          onClick={() => setTab('overlay')}
        >
          Overlay (OBS)
        </button>
        <button
          className={`dashboard__tab${tab === 'integrations' ? ' dashboard__tab--active' : ''}`}
          type="button"
          onClick={() => setTab('integrations')}
        >
          Integrações
        </button>
      </nav>

      <main className="dashboard__content">
        {tab === 'messages' && <MessagesTab />}
        {tab === 'finance' && <FinanceTab />}
        {tab === 'overlay' && <OverlayConfigTab />}
        {tab === 'integrations' && <IntegrationsTab />}
      </main>
    </div>
  );
}

/** Aba de mensagens: lista, filtro por status e ações de moderação. */
function MessagesTab() {
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);
  const [messages, setMessages] = useState<DashboardMessage[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchDashboardMessages(filter, page);
      setMessages(data.messages);
      setTotalPages(data.pagination.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar as mensagens.');
    } finally {
      setIsLoading(false);
    }
  }, [filter, page]);

  useEffect(() => {
    load();
  }, [load]);

  async function changeStatus(id: string, status: MessageStatus) {
    try {
      const updated = await updateMessageStatus(id, status);
      // Atualiza a mensagem na lista sem recarregar tudo
      setMessages((current) => current.map((m) => (m.id === id ? updated : m)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar a mensagem.');
    }
  }

  return (
    <section aria-label="Mensagens recebidas">
      <div className="filter-bar" role="group" aria-label="Filtrar por status">
        {FILTERS.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            className={`filter-bar__option${filter === value ? ' filter-bar__option--active' : ''}`}
            onClick={() => {
              setFilter(value);
              setPage(1);
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <Feedback kind="error">{error}</Feedback>}
      {isLoading && <p className="page-loading">Carregando…</p>}

      {!isLoading && messages.length === 0 && (
        <div className="card empty-state">
          <p>Nenhuma mensagem por aqui ainda.</p>
          <p className="empty-state__hint">
            Divulgue o link da sua página para receber as primeiras mensagens!
          </p>
        </div>
      )}

      <ul className="message-list">
        {messages.map((msg) => (
          <li key={msg.id} className={`card message-item message-item--${msg.status}`}>
            <div className="message-item__header">
              <strong className="message-item__sender">
                {msg.senderName}
                {msg.senderPlatform && (
                  <span className="platform-badge">via {PLATFORM_LABEL[msg.senderPlatform]}</span>
                )}
              </strong>
              <span className="message-item__amount">{formatCurrency(msg.amountCents)}</span>
            </div>
            <p className="message-item__content">{msg.message}</p>
            <div className="message-item__footer">
              <span className="message-item__meta">
                {formatDate(msg.createdAt)} · {STATUS_LABEL[msg.status]}
              </span>
              <div className="message-item__actions">
                {msg.status !== 'read' && (
                  <button
                    className="button button--small"
                    type="button"
                    onClick={() => changeStatus(msg.id, 'read')}
                  >
                    Marcar lida
                  </button>
                )}
                {msg.status !== 'hidden' ? (
                  <button
                    className="button button--small button--ghost"
                    type="button"
                    onClick={() => changeStatus(msg.id, 'hidden')}
                  >
                    Ocultar
                  </button>
                ) : (
                  <button
                    className="button button--small button--ghost"
                    type="button"
                    onClick={() => changeStatus(msg.id, 'new')}
                  >
                    Restaurar
                  </button>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="button button--small button--ghost"
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Anterior
          </button>
          <span className="pagination__info">
            Página {page} de {totalPages}
          </span>
          <button
            className="button button--small button--ghost"
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Próxima
          </button>
        </div>
      )}
    </section>
  );
}

/** Aba financeira: totais e últimas transações. */
function FinanceTab() {
  const [finance, setFinance] = useState<FinanceSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchFinance()
      .then(setFinance)
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Erro ao carregar o financeiro.'),
      );
  }, []);

  if (error) return <Feedback kind="error">{error}</Feedback>;
  if (!finance) return <p className="page-loading">Carregando…</p>;

  return (
    <section aria-label="Resumo financeiro">
      {/* Cards de resumo: empilhados no celular, lado a lado no desktop */}
      <div className="stat-grid">
        <div className="card stat">
          <span className="stat__label">Saldo (total recebido)</span>
          <strong className="stat__value">{formatCurrency(finance.balanceCents)}</strong>
        </div>
        <div className="card stat">
          <span className="stat__label">Hoje</span>
          <strong className="stat__value">{formatCurrency(finance.today.amountCents)}</strong>
          <span className="stat__detail">{finance.today.messageCount} mensagem(ns)</span>
        </div>
        <div className="card stat">
          <span className="stat__label">Mensagens no total</span>
          <strong className="stat__value">{finance.total.messageCount}</strong>
        </div>
      </div>

      <h2 className="section-title">Últimas transações</h2>

      {finance.recentTransactions.length === 0 ? (
        <div className="card empty-state">
          <p>Nenhum recebimento ainda.</p>
        </div>
      ) : (
        <ul className="transaction-list">
          {finance.recentTransactions.map((tx) => (
            <li key={tx.id} className="card transaction">
              <div className="transaction__info">
                <strong>{tx.senderName}</strong>
                <span className="transaction__date">{formatDate(tx.createdAt)}</span>
              </div>
              <span className="transaction__amount">{formatCurrency(tx.amountCents)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
